/**
 * Multi-Account Redux Thunks
 * Async actions for multi-account management
 */

import { createAsyncThunk } from '@reduxjs/toolkit';
import { AuthSecret } from '@/app/types/utils/authSecretTypes';
import { LoginPersistenceOptions } from '@/app/features/auth/utils/authSecretUtils';
import { 
  AccountCredentialsHelper,
  StoredAccount,
  accountStorage
} from '@/app/features/auth/utils/multiAccountUtils';
import { OperationLockService } from '@/app/features/auth/utils/operationLock';
import { completeAccountSwitch, completeRemoveAccount, logoutSuccess as logout } from '@/app/store/slices/authSlice';
import { clearCurrentUserData, clearUser } from '@/app/store/slices/userSlice';
import { RootState } from '@/app/store';
import { logger } from '@/app/utils/logger';

// Helper functions to replace removed accountManager service
// These provide compatibility layer for the consolidated auth services
const accountManagerCompat = {
  async addAccount(
    authSecret: AuthSecret,
    options: LoginPersistenceOptions,
    email?: string,
    profilePicture?: string
  ): Promise<string> {
    // Use AccountCredentialsHelper to create the stored account
    const account = await AccountCredentialsHelper.createStoredAccount(
      authSecret,
      options,
      email,
      profilePicture
    );
    
    await accountStorage.saveAccount(account);
    return account.id;
  },

  async getAllAccounts(): Promise<StoredAccount[]> {
    return accountStorage.getAllAccounts();
  },

  async getActiveAccount(): Promise<StoredAccount | null> {
    const activeId = accountStorage.getActiveAccountId();
    if (!activeId) return null;
    return accountStorage.getAccount(activeId);
  },

  async validateStoredAccounts(): Promise<Array<{ accountId: string; isValid: boolean; error?: string }>> {
    const accounts = await accountStorage.getAllAccounts();
    const results = [];
    
    for (const account of accounts) {
      try {
        await AccountCredentialsHelper.extractAuthSecret(account);
        results.push({ accountId: account.id, isValid: true });
      } catch (error) {
        results.push({ 
          accountId: account.id, 
          isValid: false, 
          error: error instanceof Error ? error.message : 'Validation failed' 
        });
      }
    }
    
    return results;
  }
};

/**
 * Load user preferences - used for account switching
 * Note: During initial login, preferences are loaded by Sequential Login Service
 * This function is used specifically for account switching scenarios
 */

/**
 * Add a new account
 */
export const addAccountThunk = createAsyncThunk<
  { accountId: string; accounts: StoredAccount[] },
  {
    authSecret: AuthSecret;
    options: LoginPersistenceOptions;
    email?: string;
    profilePicture?: string;
  },
  { state: RootState }
>(
  'auth/addAccount', // Updated action type prefix
  async ({ authSecret, options, email, profilePicture }, { dispatch: _dispatch, rejectWithValue }) => {
    try {
      // Add account using account manager compat
      const accountId = await accountManagerCompat.addAccount(
        authSecret,
        options,
        email,
        profilePicture
      );

      // Get updated accounts list
      const accounts = await accountManagerCompat.getAllAccounts();

      return { accountId, accounts };
    } catch (error) {
      const errorMessage = error instanceof Error ? error.message : 'Failed to add account';
      return rejectWithValue(errorMessage);
    }
  }
);

/**
 * Switch to a different account with proper sequencing and locking
 */
export const switchAccountThunk = createAsyncThunk<
  { secret: AuthSecret; accountId: string; accounts: StoredAccount[] },
  { accountId: string },
  { state: RootState }
>(
  'auth/switchAccount', // Updated action type prefix
  async ({ accountId }, { dispatch, getState: _getState, rejectWithValue }) => {
    // Check for concurrent operations
    if (OperationLockService.isLocked()) {
      logger.warn('switchAccountThunk: Operation already in progress, rejecting');
      return rejectWithValue('Another account operation is already in progress');
    }

    // Acquire operation lock
    const lockAcquired = await OperationLockService.acquireLock('global_account_operation', 10000);
    if (!lockAcquired) {
      logger.error('switchAccountThunk: Failed to acquire operation lock');
      return rejectWithValue('Failed to acquire operation lock - timeout');
    }

    try {
      logger.multiAccount('switchAccountThunk: Starting account switch with lock', { accountId });

      // Start user switching state
      const { startSwitchingUser } = await import('@/app/store/slices/userSlice');
      dispatch(startSwitchingUser());

      // Switch to the new account
      accountStorage.setActiveAccountId(accountId);
      await accountStorage.updateLastUsed(accountId);
      
      // Get the account to extract the AuthSecret
      const account = await accountStorage.getAccount(accountId);
      if (!account) {
        throw new Error(`Account ${accountId} not found`);
      }
      
      // Extract AuthSecret from the account
      const secret = await AccountCredentialsHelper.extractAuthSecret(account);
      
      // Get all accounts for the result
      const accounts = await accountManagerCompat.getAllAccounts();
      const result = {
        secret,
        accountId,
        accounts
      };

      console.log('🔥 [switchAccountThunk] Account switch completed:', {
        accountId: result.accountId,
        hasAccounts: !!result.accounts,
        accountsLength: result.accounts?.length
      });

      // CRITICAL: Load user data SEQUENTIALLY and wait for completion
      try {
        logger.multiAccount('switchAccountThunk: Loading user data sequentially', { accountId });
        const userDataResult = await dispatch(loadUserDataThunk({ accountId, forceRefresh: true })).unwrap();
        
        // Complete the user switching state ONLY after user data is loaded
        const { completeSwitchingUser } = await import('@/app/store/slices/userSlice');
        dispatch(completeSwitchingUser({ 
          accountId, 
          user: userDataResult.user 
        }));
        
        logger.multiAccount('switchAccountThunk: User data loaded and switch completed', { accountId });
      } catch (error) {
        logger.error('switchAccountThunk: Failed to load user data', error, { accountId });
        
        // Complete switching even if user data fails to prevent stuck state
        const { completeSwitchingUser } = await import('@/app/store/slices/userSlice');
        dispatch(completeSwitchingUser({ 
          accountId, 
          user: null as any // Will be handled by error state
        }));
        
        throw error; // Re-throw to trigger rejection
      }

      console.log('🔥 [switchAccountThunk] About to return result:', {
        result,
        accountId: result.accountId,
        type: 'SwitchAccountResult'
      });

      return result;
    } catch (error) {
      const errorMessage = error instanceof Error ? error.message : 'Failed to switch account';
      logger.error('switchAccountThunk: Switch failed', error, { accountId });
      return rejectWithValue(errorMessage);
    } finally {
      // Always release the lock
      OperationLockService.releaseLock('global_account_operation');
      logger.multiAccount('switchAccountThunk: Operation lock released');
    }
  }
);

/**
 * Remove an account with robust error handling and fallback logic
 */
export const removeAccountThunkRobust = createAsyncThunk<
  { 
    accounts: StoredAccount[]; 
    removedAccountId: string;
    newActiveAccountId?: string;
    newSecret?: AuthSecret;
    isCompleteLogout: boolean;
    warnings: string[];
  },
  { accountId: string; requireConfirmation?: boolean },
  { state: RootState }
>(
  'auth/removeAccountRobust', // Updated action type prefix
  async ({ accountId, requireConfirmation = true }, { dispatch, getState: _getState, rejectWithValue }) => {
    try {
      logger.info('removeAccountThunkRobust: Starting robust account removal', { accountId });

      // Get account info for confirmation
      const allAccounts = await accountStorage.getAllAccounts();
      const account = allAccounts.find(acc => acc.id === accountId);
      
      if (!account) {
        throw new Error('Account not found');
      }

      // Show confirmation dialog unless skipped
      if (requireConfirmation && typeof window !== 'undefined') {
        const { showAccountRemovalConfirm } = await import('@/app/utils/confirmDialog');
        
        const confirmed = await showAccountRemovalConfirm({
          userName: account.userName,
          email: account.email,
        });
        
        if (!confirmed) {
          logger.info('removeAccountThunkRobust: Account removal cancelled by user');
          throw new Error('Account removal cancelled by user');
        }
      }

      // Remove the account
      await accountStorage.removeAccount(accountId);
      
      // Get updated accounts list
      const accounts = await accountManagerCompat.getAllAccounts();
      
      // Determine if this was a complete logout or if we need to switch accounts
      const isCompleteLogout = accounts.length === 0;
      let newActiveAccountId: string | undefined;
      let newSecret: AuthSecret | undefined;
      
      if (!isCompleteLogout) {
        // Switch to the most recently used account
        const sortedAccounts = [...accounts].sort((a, b) => b.lastUsed - a.lastUsed);
        const newActiveAccount = sortedAccounts[0];
        
        if (newActiveAccount) {
          newActiveAccountId = newActiveAccount.id;
          accountStorage.setActiveAccountId(newActiveAccountId);
          
          // Extract the auth secret for the new active account
          try {
            newSecret = await AccountCredentialsHelper.extractAuthSecret(newActiveAccount);
          } catch (error) {
            logger.warn('removeAccountThunkRobust: Failed to extract auth secret for new active account', { error });
          }
        }
      }

      // Update Redux state based on result
      if (isCompleteLogout) {
        logger.info('removeAccountThunkRobust: Complete logout - clearing Redux state');
        dispatch(logout());
        dispatch(clearUser());
      } else if (newActiveAccountId && newSecret) {
        logger.info('removeAccountThunkRobust: Account switched - updating Redux state', {
          newActiveAccountId: newActiveAccountId
        });
        
        // Update Redux state for account switch
        dispatch(completeRemoveAccount({
          accounts: accounts,
          removedAccountId: accountId,
          newActiveAccountId: newActiveAccountId,
          newSecret: newSecret
        }));
        
        dispatch(completeAccountSwitch({
          secret: newSecret,
          accountId: newActiveAccountId,
          accounts: accounts
        }));
        
        // Load user data for the new active account (non-blocking)
        try {
          await dispatch(loadUserDataThunk({ accountId: newActiveAccountId, forceRefresh: true }));
          logger.info('removeAccountThunkRobust: User data loaded for new active account');
        } catch (userDataError) {
          logger.warn('removeAccountThunkRobust: User data loading failed (non-critical)');
          // Don't fail the entire operation for user data loading issues
        }
      } else {
        // Just update the accounts list
        dispatch(completeRemoveAccount({
          accounts: accounts,
          removedAccountId: accountId
        }));
      }

      logger.info('removeAccountThunkRobust: Account removal completed successfully', {
        removedAccountId: accountId,
        isCompleteLogout: isCompleteLogout,
        newActiveAccountId: newActiveAccountId,
        warnings: []
      });

      return {
        accounts: accounts,
        removedAccountId: accountId,
        newActiveAccountId: newActiveAccountId,
        newSecret: newSecret,
        isCompleteLogout: isCompleteLogout,
        warnings: []
      };

    } catch (error) {
      const errorMessage = error instanceof Error ? error.message : 'Failed to remove account';
      logger.error('removeAccountThunkRobust: Account removal failed', error);
      return rejectWithValue(errorMessage);
    }
  }
);

// Export with backward compatibility
export const removeAccountThunk = removeAccountThunkRobust;

/**
 * Load user data for an account
 */
export const loadUserDataThunk = createAsyncThunk<
  { accountId: string; user: any },
  { accountId: string; forceRefresh?: boolean },
  { state: RootState }
>(
  'auth/loadUserData', // Updated action type prefix
  async ({ accountId, forceRefresh = false }, { dispatch, getState, rejectWithValue }) => {
    try {
      logger.multiAccount('Starting loadUserDataThunk', { accountId, forceRefresh });
      
      const state = getState();
      
      // Check if we already have cached user data and don't need to refresh
      if (!forceRefresh && state.user.userCache[accountId]) {
        logger.multiAccount('Using cached user data for account', { accountId });
        return {
          accountId,
          user: state.user.userCache[accountId]
        };
      }

      // Get account credentials - be more flexible about active account check during operations
      let account = await accountManagerCompat.getActiveAccount();
      
      // CRITICAL DEBUG: Log the retrieved account
      logger.multiAccount('🔍 [HARD REFRESH DEBUG] Retrieved account from storage', {
        hasAccount: !!account,
        accountId: account?.id,
        loginName: account?.loginName,
        userName: account?.userName,
        email: account?.email
      });
      
      // If the requested account is not currently active, try to get it directly
      // This handles race conditions during account switching operations
      if (!account || account.id !== accountId) {
        logger.multiAccount('Requested account is not active, trying to get account directly', { accountId });
        const allAccounts = await accountStorage.getAllAccounts();
        account = allAccounts.find(acc => acc.id === accountId) || null;
        
        // CRITICAL DEBUG: Log the account found from all accounts
        logger.multiAccount('🔍 [HARD REFRESH DEBUG] Account found from getAllAccounts', {
          hasAccount: !!account,
          accountId: account?.id,
          loginName: account?.loginName,
          userName: account?.userName,
          email: account?.email
        });
        
        if (!account) {
          // Final fallback: check if this is during an account operation
          const state = getState();
          const isOperationInProgress = state.auth.isAccountSwitching || state.auth.isRemovingAccount;
          
          if (isOperationInProgress) {
            logger.multiAccount('Account operation in progress, deferring user data loading');
            // Return cached data if available during operations
            if (state.user.userCache[accountId]) {
              return {
                accountId,
                user: state.user.userCache[accountId]
              };
            }
            // Skip loading during operations to prevent race conditions
            throw new Error(`Account ${accountId} not available during operation - will retry after completion`);
          }
          
          throw new Error(`Account ${accountId} not found in stored accounts`);
        }
        
        // If we found the account but it's not active, this might be during a switching operation
        logger.multiAccount('Found account but not active, proceeding with data loading', { accountId: account.id });
      }

      logger.multiAccount('Creating user data for account', { email: account.email, userName: account.userName });
      logger.multiAccount('Full account object', { account });

      // Fetch real user data from the API instead of generating mock data
      try {
        logger.multiAccount('Fetching real user data from API...');
        
        // CRITICAL DEBUG: Log the account object to verify loginName
        logger.multiAccount('🔍 [HARD REFRESH DEBUG] Account object details', {
          accountId: account.id,
          loginName: account.loginName,
          userName: account.userName,
          email: account.email,
          loginNameType: typeof account.loginName,
          userNameType: typeof account.userName
        });
        
        // Use the existing API to get user data by login name
        const { API_BASE_URL, API_USERS_BY_LOGIN_NAME } = await import('@/app/constants/apiConstants');
        const apiProxy = (await import('@/app/features/shared/utils/apiProxy')).default;
        
        const loginName = account.loginName || account.email;
        
        // CRITICAL DEBUG: Log what we're about to use
        logger.multiAccount('🔍 [HARD REFRESH DEBUG] LoginName value to use', {
          loginName,
          source: account.loginName ? 'account.loginName' : 'account.email',
          accountLoginName: account.loginName,
          accountEmail: account.email
        });
        
        const params = new URLSearchParams();
        params.append('PartitionKey', 'sys:smart_matrix'); // Required parameter
        params.append('LoginName', loginName); // Correct parameter name
        const queryString = params.toString();
        const url = `${API_BASE_URL}${API_USERS_BY_LOGIN_NAME}?${queryString}`;
        
        logger.multiAccount('Calling API', { url });
        logger.multiAccount('API parameters', { PartitionKey: 'sys:smart_matrix', LoginName: loginName });
        logger.multiAccount('Full URL being called', { url });
        
        const response = await apiProxy({
          method: 'GET',
          url: url,
        });
        
        logger.multiAccount('API response', { response: response.data });
        
        // Enhanced debugging for API response structure
        console.log('🔍 [API DEBUG] ========== API RESPONSE ANALYSIS ==========');
        console.log('🔍 [API DEBUG] Full response object:', response);
        console.log('🔍 [API DEBUG] Response.data:', response.data);
        console.log('🔍 [API DEBUG] Response.data stringified:', JSON.stringify(response.data, null, 2));
        
        logger.multiAccount('API response structure check', {
          hasSucceeded: !!response.data.succeeded,
          hasData: !!response.data.data,
          hasDataCapital: !!response.data.Data,
          dataKeys: response.data.data ? Object.keys(response.data.data) : (response.data.Data ? Object.keys(response.data.Data) : 'no data'),
          dataType: typeof response.data.data,
          dataIsNull: response.data.data === null,
          dataIsUndefined: response.data.data === undefined,
          fullResponse: JSON.stringify(response.data, null, 2)
        });
        
        // Check different possible response structures
        let apiUser = null;
        
        // Try to get the data object (could be lowercase or uppercase)
        const dataObj = response.data.data || response.data.Data;
        
        console.log('🔍 [API DEBUG] dataObj extracted:', dataObj);
        console.log('🔍 [API DEBUG] dataObj type:', typeof dataObj);
        console.log('🔍 [API DEBUG] dataObj keys:', dataObj ? Object.keys(dataObj) : 'null/undefined');
        
        if (response.data.succeeded && dataObj) {
          console.log('✅ [API DEBUG] Response succeeded and dataObj exists, checking for user properties...');
          
          // Log all properties in dataObj
          if (dataObj && typeof dataObj === 'object') {
            Object.keys(dataObj).forEach(key => {
              console.log(`🔍 [API DEBUG] dataObj.${key}:`, dataObj[key]);
              console.log(`🔍 [API DEBUG] dataObj.${key} type:`, typeof dataObj[key]);
            });
          }
          
          // Try different possible property names for the user data
          if (dataObj.sysUser) {
            apiUser = dataObj.sysUser;
            logger.multiAccount('✅ Found user data in data.sysUser');
            console.log('✅ [API DEBUG] Using dataObj.sysUser');
          } else if (dataObj.user) {
            apiUser = dataObj.user;
            logger.multiAccount('✅ Found user data in data.user');
            console.log('✅ [API DEBUG] Using dataObj.user');
          } else if (dataObj.User) {
            apiUser = dataObj.User;
            logger.multiAccount('✅ Found user data in data.User');
            console.log('✅ [API DEBUG] Using dataObj.User');
          } else {
            console.log('⚠️ [API DEBUG] No user/User/sysUser property found, checking if dataObj itself is the user...');
            logger.multiAccount('No user data found in expected properties, checking direct data');
            // Maybe the user data is directly in data
            if (dataObj.id || dataObj.Id) {
              apiUser = dataObj;
              logger.multiAccount('✅ Using data directly as user object');
              console.log('✅ [API DEBUG] Using dataObj directly as user');
            } else {
              console.log('❌ [API DEBUG] dataObj has no id/Id property, cannot use as user');
            }
          }
        } else if (response.data.succeeded) {
          console.log('⚠️ [API DEBUG] Response succeeded but dataObj is null/undefined, checking root level...');
          // Maybe data is null but user is at root level
          logger.multiAccount('Data is null/undefined, checking root level for user');
          if (response.data.user) {
            apiUser = response.data.user;
            logger.multiAccount('✅ Found user data at root level (data.user)');
            console.log('✅ [API DEBUG] Using root level response.data.user');
          } else if (response.data.User) {
            apiUser = response.data.User;
            logger.multiAccount('✅ Found user data at root level (data.User)');
            console.log('✅ [API DEBUG] Using root level response.data.User');
          } else {
            console.log('❌ [API DEBUG] No user property at root level either');
          }
        } else {
          console.log('❌ [API DEBUG] Response.data.succeeded is false or missing:', response.data.succeeded);
        }
        
        console.log('🔍 [API DEBUG] Final apiUser:', apiUser);
        console.log('🔍 [API DEBUG] ========== END API RESPONSE ANALYSIS ==========');
        
        if (apiUser && (apiUser.id || apiUser.Id)) {
          logger.multiAccount('Using real user data from API', { userId: apiUser.id || apiUser.Id });
          
          // Cache the real user data
          const { cacheUserData } = await import('@/app/store/slices/userSlice');
          dispatch(cacheUserData({ accountId, user: apiUser }));
          
          // Load and apply user preferences for account switching
          // Only load preferences when forceRefresh=true (account switching)
          // During initial login, preferences are loaded by Sequential Login Service
          if (forceRefresh) {
            logger.multiAccount('User preferences are now loaded automatically in authManager.retrieveAndStoreUserInfo()', { userId: apiUser.id || apiUser.Id });
            // Note: Preference loading is now handled in authManager.retrieveAndStoreUserInfo() during account switch
            // This ensures preferences are loaded and applied immediately when switching accounts
          } else {
            logger.multiAccount('Skipping preference loading (handled by Sequential Login Service)', { userId: apiUser.id || apiUser.Id });
          }
          
          logger.multiAccount('Returning real user data', { userId: apiUser.id || apiUser.Id });
          return { accountId, user: apiUser };
        } else {
          logger.warn('API call succeeded but no user data returned, falling back to mock data');
          logger.warn('Response data structure', { responseData: response.data });
          throw new Error('No user data returned from API');
        }
      } catch (apiError) {
        logger.error('Failed to fetch real user data, falling back to mock data', apiError);
        
        // Fallback to mock data if API call fails (for backward compatibility)
        // Generate consistent ID based on account email/username to ensure same user gets same ID
        const generateConsistentId = (identifier: string): number => {
          logger.multiAccount('Input identifier for ID generation', { identifier });
          let hash = 0;
          for (let i = 0; i < identifier.length; i++) {
            const char = identifier.charCodeAt(i);
            hash = ((hash << 5) - hash) + char;
            hash = hash & hash; // Convert to 32-bit integer
          }
          // Ensure positive number and reasonable range (1-999999)
          const result = Math.abs(hash % 999999) + 1;
          logger.multiAccount('Generated hash and final ID', { hash, finalId: result });
          return result;
        };

        const identifier = account.email || account.userName;
        logger.multiAccount('Account data for ID generation', {
          email: account.email,
          userName: account.userName,
          selectedIdentifier: identifier
        });
        
        const consistentId = generateConsistentId(identifier);
        
        // Generate consistent GUID based on account identifier as well
        const generateConsistentGuid = (identifier: string): string => {
          // Create a deterministic UUID based on the identifier
          // This ensures the same account always gets the same GUID
          let hash = 0;
          for (let i = 0; i < identifier.length; i++) {
            const char = identifier.charCodeAt(i);
            hash = ((hash << 5) - hash) + char;
            hash = hash & hash;
          }
          
          // Convert to hex and pad to create UUID format
          const hex = Math.abs(hash).toString(16).padStart(8, '0');
          return `${hex.slice(0, 8)}-${hex.slice(0, 4)}-4${hex.slice(1, 4)}-8${hex.slice(0, 3)}-${hex.slice(0, 12)}`;
        };
        
        const consistentGuid = generateConsistentGuid(account.email || account.userName);
        
        logger.multiAccount('Generated consistent ID and GUID for account', { 
          consistentId, 
          consistentGuid, 
          account: account.email || account.userName 
        });
        
        const mockUser = {
          id: consistentId,
          guid: consistentGuid,
          isDeleted: false,
          status: 'Active',
          createdAt: new Date().toISOString(),
          createdBy: 'System',
          modifiedAt: new Date().toISOString(),
          modifiedBy: 'System',
          deletedAt: null,
          deletedBy: null,
          partitionKey: 'sys:smart_matrix',
          type: 'System',
          userName: account.userName,
          displayName: account.userName,
          givenName: account.userName.split(' ')[0] || account.userName,
          surname: account.userName.split(' ')[1] || '',
          email: account.email,
          profilePictureUrl: null,
          logins: [],
          roles: []
        };

        // Cache the mock user data
        const { cacheUserData } = await import('../slices/userSlice');
        dispatch(cacheUserData({ accountId, user: mockUser }));

        // Load and apply user preferences for mock user during account switching
        // Only load preferences when forceRefresh=true (account switching)
        if (forceRefresh) {
          logger.multiAccount('Mock user preferences are now loaded automatically in authManager.retrieveAndStoreUserInfo()', { consistentId });
          // Note: Preference loading is now handled in authManager.retrieveAndStoreUserInfo() during account switch
          // This ensures preferences are loaded and applied immediately when switching accounts
        } else {
          logger.multiAccount('Skipping mock user preference loading (handled by Sequential Login Service)', { consistentId });
        }

        logger.multiAccount('Returning fallback mock user data', { userId: mockUser.id });
        return { accountId, user: mockUser };
      }
    } catch (error) {
      logger.error('loadUserDataThunk error', error);
      const errorMessage = error instanceof Error ? error.message : 'Failed to load user data';
      return rejectWithValue(errorMessage);
    }
  }
);

/**
 * Validate all stored accounts
 */
export const validateAccountsThunk = createAsyncThunk<
  { validationTime: number; errors: Record<string, string> },
  void,
  { state: RootState }
>(
  'auth/validateAccounts', // Updated action type prefix
  async (_, { dispatch: _dispatch, rejectWithValue }) => {
    try {
      const validationResults = await accountManagerCompat.validateStoredAccounts();
      const validationTime = Date.now();
      const errors: Record<string, string> = {};

      // Collect validation errors
      validationResults.forEach(result => {
        if (!result.isValid && result.error) {
          errors[result.accountId] = result.error;
        }
      });

      return { validationTime, errors };
    } catch (error) {
      const errorMessage = error instanceof Error ? error.message : 'Failed to validate accounts';
      return rejectWithValue(errorMessage);
    }
  }
);

/**
 * Refresh accounts list
 */
export const refreshAccountsThunk = createAsyncThunk<
  StoredAccount[],
  void,
  { state: RootState }
>(
  'auth/refreshAccounts', // Updated action type prefix
  async (_, { dispatch: _dispatch, rejectWithValue }) => {
    try {
      const accounts = await accountManagerCompat.getAllAccounts();
      return accounts;
    } catch (error) {
      const errorMessage = error instanceof Error ? error.message : 'Failed to refresh accounts';
      return rejectWithValue(errorMessage);
    }
  }
);

/**
 * Initialize multi-account system
 */
export const initializeMultiAccountThunk = createAsyncThunk<
  { 
    accounts: StoredAccount[];
    activeAccount?: { secret: AuthSecret; accountId: string };
    cleanupReport?: { duplicatesFound: number; duplicatesRemoved: number; errors: string[] };
    migrationReport?: { legacyDataFound: boolean; migrationSuccessful: boolean; error?: string };
  },
  void,
  { state: RootState }
>(
  'auth/initialize', // Updated action type prefix
  async (_, { dispatch, rejectWithValue }) => {
    try {
      // Initialize the multi-account system
      const { initializeMultiAccountSystem } = await import('../../features/auth/utils/multiAccountUtils');
      await initializeMultiAccountSystem();

      // Get all accounts first
      let accounts = await accountManagerCompat.getAllAccounts();

      // Always run cleanup to prevent duplicates, regardless of account count
      // This is especially important in production where duplicates can appear during first login
      let cleanupReport: { duplicatesFound: number; duplicatesRemoved: number; errors: string[] } | undefined;
      
      if (accounts.length > 0) {
        const { AccountCredentialsHelper } = await import('../../features/auth/utils/accountStorage');
        cleanupReport = await AccountCredentialsHelper.cleanupDuplicateAccounts(accountStorage);
        
        // Refresh accounts list after cleanup
        accounts = await accountManagerCompat.getAllAccounts();
        
        if (cleanupReport.duplicatesRemoved > 0) {
          logger.info('initializeMultiAccountThunk: Duplicate accounts cleaned up', {
            duplicatesFound: cleanupReport.duplicatesFound,
            duplicatesRemoved: cleanupReport.duplicatesRemoved,
            accountsAfterCleanup: accounts.length
          });
          
          // Temporary production logging to monitor the fix
          if (process.env.NODE_ENV === 'production') {
            console.log('SmartMatrix: System initialization cleaned up duplicate accounts', {
              duplicatesFound: cleanupReport.duplicatesFound,
              duplicatesRemoved: cleanupReport.duplicatesRemoved,
              accountsAfterCleanup: accounts.length,
              timestamp: new Date().toISOString()
            });
          }
        }
      }

      // Check if there's an active account
      let activeAccount = await accountManagerCompat.getActiveAccount();
      
      // If no active account but accounts exist, set the first one as active
      if (!activeAccount && accounts.length > 0) {
        const firstAccount = accounts[0];
        accountStorage.setActiveAccountId(firstAccount.id);
        activeAccount = firstAccount;
      }
      
      let activeAccountData: { secret: AuthSecret; accountId: string } | undefined;

      // If we have an active account, extract credentials and skip legacy checks
      if (activeAccount) {
        try {
          // Migrate metadata if needed (backward compatibility)
          const { AccountCredentialsHelper } = await import('../../features/auth/utils/accountStorage');
          await AccountCredentialsHelper.migrateAccountMetadata(activeAccount);
          
          // Extract credentials (this also syncs metadata to storage)
          const secret = await AccountCredentialsHelper.extractAuthSecret(activeAccount);
          activeAccountData = { secret, accountId: activeAccount.id };
          
          // Update last used
          await accountStorage.updateLastUsed(activeAccount.id);
          
          // Load user data from backend using the new system
          try {
            const userDataResult = await dispatch(loadUserDataThunk({ accountId: activeAccount.id, forceRefresh: false }));
            
            // Load user preferences for login restoration scenario
            // This ensures preferences are applied when user returns to the app
            if (loadUserDataThunk.fulfilled.match(userDataResult) && userDataResult.payload.user?.id) {
              logger.multiAccount('User preferences are now loaded automatically in authManager.retrieveAndStoreUserInfo() during login restoration', { 
                userId: userDataResult.payload.user.id,
                accountId: activeAccount.id 
              });
              // Note: Preference loading is now handled in authManager.retrieveAndStoreUserInfo() during account operations
              // This ensures preferences are loaded and applied immediately during login restoration
            }
          } catch (error) {
            logger.error('initializeMultiAccountThunk: Failed to load user data', error);
          }
        } catch (error) {
          logger.error('initializeMultiAccountThunk: Failed to extract credentials for active account', error);
          
          // If credential extraction fails, try to remove the corrupted account and use legacy auth
          try {
            logger.multiAccount('Attempting to recover from corrupted account data...');
            await accountStorage.removeAccount(activeAccount.id);
            
            // Try to use legacy auth data if available
            const { getSecret } = await import('../../features/auth/utils/authSecretUtils');
            const legacySecret = getSecret();
            
            if (legacySecret) {
              logger.multiAccount('Using legacy auth data for recovery');
              activeAccountData = { secret: legacySecret, accountId: 'legacy-recovery' };
              
              // Try to re-add the account with fresh encryption
              try {
                const options: LoginPersistenceOptions = {
                  rememberMe: true,
                  persistenceDays: 30
                };
                
                const newAccountId = await accountManagerCompat.addAccount(
                  legacySecret,
                  options,
                  legacySecret.loginName
                );
                
                activeAccountData = { secret: legacySecret, accountId: newAccountId };
                logger.multiAccount('Account recovery successful with new account ID', { newAccountId });
              } catch (reAddError) {
                logger.error('Failed to re-add account during recovery', reAddError);
              }
            }
          } catch (recoveryError) {
            logger.error('Account recovery failed', recoveryError);
          }
        }
        
        // Skip legacy checks and cleanup when we have an active account
        return { accounts, activeAccount: activeAccountData };
      }

      // Only check for legacy auth if no active account exists
      const migrationReport: { legacyDataFound: boolean; migrationSuccessful: boolean; error?: string } = { 
        legacyDataFound: false, 
        migrationSuccessful: false 
      };
      
      try {
        const { getSecret, attemptAuthRecovery } = await import('../../features/auth/utils/authSecretUtils');
        
        // Try to get legacy auth data
        let legacySecret = getSecret();
        if (!legacySecret) {
          legacySecret = attemptAuthRecovery();
        }
        
        if (legacySecret) {
          migrationReport.legacyDataFound = true;
          
          // Check if this user already exists in multi-account system
          const { AccountCredentialsHelper, accountStorage } = await import('../../features/auth/utils/accountStorage');
          const existingAccount = await AccountCredentialsHelper.findExistingAccount(
            legacySecret.loginName,
            accountStorage,
            legacySecret.loginName
          );
          
          if (!existingAccount) {
            // Migrate legacy user to multi-account system
            const accountId = await accountManagerCompat.addAccount(
              legacySecret,
              { rememberMe: true, persistenceDays: 7 },
              legacySecret.loginName
            );
            
            migrationReport.migrationSuccessful = true;
            
            // Get the migrated account
            const migratedAccount = await accountStorage.getAccount(accountId);
            if (migratedAccount) {
              const secret = await AccountCredentialsHelper.extractAuthSecret(migratedAccount);
              activeAccountData = { secret, accountId };
            }
            
          } else {
            await accountStorage.updateLastUsed(existingAccount.id);
            await accountStorage.setActiveAccountId(existingAccount.id);
            migrationReport.migrationSuccessful = true;
            
            const secret = await AccountCredentialsHelper.extractAuthSecret(existingAccount);
            activeAccountData = { secret, accountId: existingAccount.id };
          }
          
          // Clear legacy auth data after successful migration
          const { clearSecret } = await import('../../features/auth/utils/authSecretUtils');
          clearSecret();
        }
      } catch (migrationError) {
        logger.error('initializeMultiAccountThunk: Migration failed', migrationError);
        migrationReport.error = migrationError instanceof Error ? migrationError.message : 'Unknown migration error';
      }

      // Clean up duplicate accounts only if we have multiple accounts
      // Note: This cleanup was already performed earlier in the initialization process
      // This section is kept for backward compatibility but should not be needed
      if (!cleanupReport && accounts.length > 1) {
        const { AccountCredentialsHelper } = await import('../../features/auth/utils/accountStorage');
        cleanupReport = await AccountCredentialsHelper.cleanupDuplicateAccounts(accountStorage);
      }

      return { accounts, activeAccount: activeAccountData, cleanupReport, migrationReport };
    } catch (error) {
      logger.error('initializeMultiAccountThunk: Initialization failed', error);
      const errorMessage = error instanceof Error ? error.message : 'Failed to initialize multi-account system';
      return rejectWithValue(errorMessage);
    }
  }
);

/**
 * Cleanup multi-account system
 */
export const cleanupMultiAccountThunk = createAsyncThunk<
  void,
  void,
  { state: RootState }
>(
  'auth/cleanup', // Updated action type prefix
  async (_, { dispatch }) => {
    try {
      const { cleanupMultiAccountSystem } = await import('../../features/auth/utils/multiAccountUtils');
      await cleanupMultiAccountSystem();
      
      // Clear all Redux state
      dispatch(logout());
    } catch (error) {
      logger.error('Failed to cleanup multi-account system', error);
      // Still clear Redux state even if cleanup fails
      dispatch(logout());
    }
  }
);

/**
 * Complete account switch with user data loading
 */
export const completeAccountSwitchWithUserData = createAsyncThunk<
  void,
  { secret: AuthSecret; accountId: string; accounts: StoredAccount[] },
  { state: RootState }
>(
  'auth/completeAccountSwitchWithUserData', // Updated action type prefix
  async ({ secret, accountId, accounts }, { dispatch }) => {
    // Update auth state
    dispatch(completeAccountSwitch({ secret, accountId, accounts }));
    
    // Clear current user data to ensure clean switch
    dispatch(clearCurrentUserData());
    
    // Load user data for the new account
    dispatch(loadUserDataThunk({ accountId }));
  }
);

/**
 * Add account with user data loading
 */
export const addAccountWithUserData = createAsyncThunk<
  void,
  {
    authSecret: AuthSecret;
    options: LoginPersistenceOptions;
    email?: string;
    profilePicture?: string;
    switchToNewAccount?: boolean;
  },
  { state: RootState }
>(
  'auth/addAccountWithUserData', // Updated action type prefix
  async ({ authSecret, options, email, profilePicture, switchToNewAccount = false }, { dispatch }) => {
    // Add the account
    const result = await dispatch(addAccountThunk({ authSecret, options, email, profilePicture }));
    
    if (addAccountThunk.fulfilled.match(result)) {
      const { accountId } = result.payload;
      
      // Load user data for the new account
      dispatch(loadUserDataThunk({ accountId }));
      
      // Switch to new account if requested
      if (switchToNewAccount) {
        dispatch(switchAccountThunk({ accountId }));
      }
    }
  }
);

/**
 * Comprehensive session validation with cleanup
 */
export const validateAndCleanupSessionsThunk = createAsyncThunk<
  {
    validationReport: any;
    cleanupReport: any;
  },
  { performCleanup?: boolean },
  { state: RootState }
>(
  'auth/validateAndCleanupSessions', // Updated action type prefix
  async ({ performCleanup = true }, { dispatch: _dispatch, rejectWithValue }) => {
    try {
      // Simplified validation - check all stored accounts
      const accounts = await accountStorage.getAllAccounts();
      const validationResults = await accountManagerCompat.validateStoredAccounts();
      
      const validationReport = {
        totalAccounts: accounts.length,
        validAccounts: validationResults.filter(r => r.isValid).length,
        invalidAccounts: validationResults.filter(r => !r.isValid).length,
        results: validationResults
      };
      
      let cleanupReport = null;
      if (performCleanup) {
        // Remove invalid accounts
        const invalidAccounts = validationResults.filter(r => !r.isValid);
        for (const result of invalidAccounts) {
          try {
            await accountStorage.removeAccount(result.accountId);
          } catch (error) {
            logger.warn('Failed to remove invalid account', { accountId: result.accountId, error });
          }
        }
        
        cleanupReport = {
          accountsRemoved: invalidAccounts.length,
          errors: []
        };
      }

      return {
        validationReport,
        cleanupReport
      };
    } catch (error) {
      const errorMessage = error instanceof Error ? error.message : 'Failed to validate and cleanup sessions';
      return rejectWithValue(errorMessage);
    }
  }
);

/**
 * Handle individual session expiration
 */
export const handleSessionExpirationThunk = createAsyncThunk<
  { accountId: string; action: 'removed' | 'switched' | 'marked_for_reauth' },
  { accountId: string },
  { state: RootState }
>(
  'auth/handleSessionExpiration', // Updated action type prefix
  async ({ accountId }, { dispatch: _dispatch, getState, rejectWithValue }) => {
    try {
      const state = getState();
      const wasActiveAccount = state.auth.activeAccountId === accountId;
      
      // Simplified expiration handling - just remove the account
      await accountStorage.removeAccount(accountId);
      
      // Determine what action was taken
      const newState = getState();
      const isStillActive = newState.auth.activeAccountId === accountId;
      const accountStillExists = newState.auth.availableAccounts.some(acc => acc.id === accountId);
      
      let action: 'removed' | 'switched' | 'marked_for_reauth';
      
      if (!accountStillExists) {
        action = 'removed';
      } else if (wasActiveAccount && !isStillActive) {
        action = 'switched';
      } else {
        action = 'marked_for_reauth';
      }

      return { accountId, action };
    } catch (error) {
      const errorMessage = error instanceof Error ? error.message : 'Failed to handle session expiration';
      return rejectWithValue(errorMessage);
    }
  }
);

/**
 * Start periodic session validation
 */
export const startPeriodicValidationThunk = createAsyncThunk<
  { started: boolean; frequency: number },
  void,
  { state: RootState }
>(
  'auth/startPeriodicValidation', // Updated action type prefix
  async (_, { dispatch: _dispatch }) => {
    try {
      // Simplified - no periodic validation needed with consolidated services
      logger.info('Periodic validation not implemented with consolidated services');
      return {
        started: false,
        frequency: 0
      };
    } catch (error) {
      logger.error('Failed to start periodic validation', error);
      return {
        started: false,
        frequency: 0
      };
    }
  }
);

/**
 * Stop periodic session validation
 */
export const stopPeriodicValidationThunk = createAsyncThunk<
  { stopped: boolean },
  void,
  { state: RootState }
>(
  'auth/stopPeriodicValidation', // Updated action type prefix
  async (_, { dispatch: _dispatch }) => {
    try {
      // Simplified - no periodic validation to stop
      logger.info('Periodic validation not implemented with consolidated services');
      return { stopped: true };
    } catch (error) {
      logger.error('Failed to stop periodic validation', error);
      return { stopped: false };
    }
  }
);

/**
 * Initiate add account workflow
 */
export const initiateAddAccountThunk = createAsyncThunk<
  { redirectUrl: string; sessionId: string; returnUrl: string },
  { returnUrl?: string },
  { state: RootState }
>(
  'auth/initiateAddAccount', // Updated action type prefix
  async ({ returnUrl }, { dispatch: _dispatch, rejectWithValue }) => {
    try {
      // Simplified - return basic redirect info
      const sessionId = `session_${Date.now()}`;
      const redirectUrl = returnUrl || '/main';
      
      return {
        redirectUrl,
        sessionId,
        returnUrl: returnUrl || '/main'
      };
    } catch (error) {
      const errorMessage = error instanceof Error ? error.message : 'Failed to initiate add account';
      return rejectWithValue(errorMessage);
    }
  }
);

/**
 * Process account addition after authentication
 */
export const processAddAccountThunk = createAsyncThunk<
  {
    success: boolean;
    accountId?: string;
    error?: string;
    switchedToNewAccount: boolean;
    totalAccounts: number;
  },
  {
    authSecret: AuthSecret;
    options: LoginPersistenceOptions;
    email?: string;
    profilePicture?: string;
    switchToNewAccount?: boolean;
  },
  { state: RootState }
>(
  'auth/processAddAccount', // Updated action type prefix
  async ({ authSecret, options, email, profilePicture, switchToNewAccount = true }, { dispatch, rejectWithValue }) => {
    // Check for concurrent operations to prevent race conditions
    if (OperationLockService.isLocked()) {
      logger.warn('processAddAccountThunk: Operation already in progress, rejecting');
      return rejectWithValue('Another account operation is already in progress');
    }

    // Acquire operation lock to prevent concurrent operations
    const lockAcquired = await OperationLockService.acquireLock('global_account_operation', 15000);
    if (!lockAcquired) {
      logger.error('processAddAccountThunk: Failed to acquire operation lock');
      return rejectWithValue('Failed to acquire operation lock - timeout');
    }

    try {
      logger.multiAccount('processAddAccountThunk: Starting account addition process with lock', { 
        email, 
        switchToNewAccount 
      });

      // STEP 1: Add the account using accountManagerCompat
      const accountId = await accountManagerCompat.addAccount(
        authSecret,
        options,
        email,
        profilePicture
      );

      // Get all accounts
      const allAccounts = await accountManagerCompat.getAllAccounts();

      logger.multiAccount('processAddAccountThunk: Account added successfully', { accountId });

      // STEP 2: If requested, switch to the new account
      let switchedToNewAccount = false;
      if (switchToNewAccount) {
        logger.multiAccount('processAddAccountThunk: Switching to new account', { accountId });
        
        try {
          await dispatch(switchAccountThunk({ accountId })).unwrap();
          switchedToNewAccount = true;
          logger.multiAccount('processAddAccountThunk: Successfully switched to new account', { accountId });
        } catch (switchError) {
          logger.error('processAddAccountThunk: Failed to switch to new account', switchError);
          // Don't fail the entire operation if switch fails
        }
      }

      // STEP 3: Refresh accounts list
      try {
        await dispatch(refreshAccountsThunk()).unwrap();
        logger.multiAccount('processAddAccountThunk: Accounts list refreshed');
      } catch (refreshError) {
        logger.warn('processAddAccountThunk: Failed to refresh accounts list (non-critical)', refreshError);
      }

      const result = {
        success: true,
        accountId,
        switchedToNewAccount,
        totalAccounts: allAccounts.length
      };

      logger.multiAccount('processAddAccountThunk: Account addition completed', { result });

      return result;
    } catch (error) {
      logger.error('processAddAccountThunk: Account addition process failed', error);
      const errorMessage = error instanceof Error ? error.message : 'Failed to process add account';
      return rejectWithValue(errorMessage);
    } finally {
      // Always release the lock
      OperationLockService.releaseLock('global_account_operation');
      logger.multiAccount('processAddAccountThunk: Operation lock released');
    }
  }
);

/**
 * Cancel add account workflow
 */
export const cancelAddAccountThunk = createAsyncThunk<
  { cancelled: boolean },
  void,
  { state: RootState }
>(
  'auth/cancelAddAccount', // Updated action type prefix
  async (_, { dispatch: _dispatch }) => {
    try {
      // Simplified - no workflow to cancel
      logger.info('Cancel add account - no workflow to cancel with consolidated services');
      return { cancelled: true };
    } catch (error) {
      logger.error('Failed to cancel add account', error);
      return { cancelled: false };
    }
  }
);

/**
 * Validate add account eligibility
 */
export const validateAddAccountEligibilityThunk = createAsyncThunk<
  {
    eligible: boolean;
    reason?: string;
    maxAccountsReached: boolean;
    currentAccountCount: number;
    maxAccounts: number;
  },
  void,
  { state: RootState }
>(
  'auth/validateAddAccountEligibility', // Updated action type prefix
  async (_, { dispatch: _dispatch, rejectWithValue }) => {
    try {
      // Simplified eligibility check
      const accounts = await accountStorage.getAllAccounts();
      const maxAccounts = 10; // Default max accounts
      const eligible = accounts.length < maxAccounts;
      const maxAccountsReached = accounts.length >= maxAccounts;
      
      return {
        eligible,
        reason: eligible ? undefined : 'Maximum number of accounts reached',
        maxAccountsReached,
        currentAccountCount: accounts.length,
        maxAccounts
      };
    } catch (error) {
      const errorMessage = error instanceof Error ? error.message : 'Failed to validate eligibility';
      return rejectWithValue(errorMessage);
    }
  }
);
